import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format, addDays, addMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isSameDay, isSameMonth, getDay } from "date-fns";
import { MapPin, ChevronRight, ChevronLeft, ChevronDown, CheckCircle, Check, Truck, Wrench, User, Phone, ClipboardCheck, PenTool, X, Search, FileText, AlertTriangle, Plus, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PillRail, Pill } from "@/components/common/Segmented";
import { DateJump } from "@/components/common/DateJump";
import { btn } from "@/components/common/btn";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { getTechnicians, listDispatches, updateWOStatus, updateDispatchStatus, updateTechLocation, getGpsWindow, getInspections, getSignoffSummary, customerSignoff, addFieldNote, getFieldNotes, getWOTechInfo, getWOTechDocUrl, getPartDocUrl, checkAllCardProgress, getInspectionTemplates, createInspectionFromTemplate, markDispatchNotesSeen, addContractFieldNote, updateDispatch, appendTodayTimesheetNote} from "@/lib/api";
import { JobTag, isContract, bandColor, kindMeta, jobKindOf } from "@/components/dispatch/JobTag";
import { CrewLine, PriorTechsLine } from "@/components/tech/CrewLine";
import { SiteAccessLine } from "@/components/tech/AccessNotes";
import ClockOutNoteModal from "@/components/tech/ClockOutNoteModal";
import PeerCard from "@/components/tech/PeerCard";
import FieldLookup from "@/components/tech/lookup/FieldLookup";
import FieldMapLite from "@/components/tech/FieldMapLite";
import SubTabs from "@/components/common/SubTabs";
import { WeekDayHeader } from "@/components/common/WeekGrid";
import { StopPills } from "@/components/dispatch/StopPills";
import WOCompletionModal from "@/components/WOCompletionModal";
import DoneForDayModal from "@/components/DoneForDayModal";
import SignatureCanvas from "react-signature-canvas";
import { abbreviateName, formatNoteDate } from "@/lib/noteFormat";
import { progressKeyFor, isPartChecked } from "@/lib/cardProgress";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n";
import { Tx, txNote } from "@/i18n/Tx";
import { NoteText } from "@/i18n/NoteText";
import { es as esLocale } from "date-fns/locale";
import LocationRequiredGate from "@/components/LocationRequiredGate";
import OffBlockBadge from "@/components/DispatchOffBlock";
import DispatchNotesModal from "@/components/dispatch/DispatchNotesModal";
import { useDispatchCategories } from "@/lib/dispatchCategories";
import { checkPermission, onChange as onPermissionChange } from "@/lib/locationPermission";
import { enqueue as enqueueOffline, pendingCount as offlinePendingCount, subscribe as subscribeOfflineQueue } from "@/lib/offlineQueue";
import {
  precacheBundle, cachedBundle, initInspectionSync, flush as flushInspections,
  pendingCount as pendingInspectionCount, subscribe as subscribeInspectionSync,
  inspectionState as computeInspectionState, lastSyncAt,
} from "@/lib/inspectionSync";
// iter167 — native shell (Capacitor) abstraction over GPS.  No-ops on
// the web; switches to background plugin when running inside iOS/Android.
import {
  startTracking as nativeStartTracking,
  stopTracking as nativeStopTracking,
  setForegroundInterval as nativeSetForegroundInterval,
} from "@/lib/nativeShell";

// #145 (Group G): cap how many notes render at once. A WO with 200+
// dispatch/field notes (long-running maintenance jobs accumulate them)
// freezes a phone Safari trying to lay out the card. We default to
// the most-recent 5 and offer a "Show all" toggle. Once expanded we
// re-render all entries — at that point the tech opted in.
const NOTE_PREVIEW_COUNT = 5;
function NoteList({ notes, variant, dispatchId, renderRow }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  if (!Array.isArray(notes) || notes.length === 0) return null;
  const ordered = [...notes];
  const shown = expanded ? ordered : ordered.slice(-NOTE_PREVIEW_COUNT);
  const isDispatch = variant === "dispatch";
  const wrapClasses = isDispatch
    ? "text-[9px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mt-1 space-y-0.5"
    : "text-[9px] text-slate-700 bg-slate-50 border border-slate-200 rounded px-1.5 py-1 mt-1 space-y-0.5";
  const headerClasses = isDispatch
    ? "text-[8px] font-semibold text-amber-800 uppercase tracking-wide"
    : "text-[8px] font-semibold text-slate-500 uppercase tracking-wide";
  const hidden = ordered.length - shown.length;
  return (
    <div className={wrapClasses} data-testid={isDispatch ? `dispatch-note-${dispatchId}` : `field-notes-${dispatchId}`}>
      <div className={headerClasses}>{isDispatch ? t("Dispatch Notes") : t("Field Notes")}</div>
      {hidden > 0 && !expanded && (
        <button
          type="button"
          className="text-[9px] underline text-slate-500 hover:text-slate-700"
          onClick={() => setExpanded(true)}
          data-testid={`note-show-more-${dispatchId}-${variant}`}
        >
          {t(hidden === 1 ? "Show {n} earlier note" : "Show {n} earlier notes", { n: hidden })}
        </button>
      )}
      {expanded && ordered.length > NOTE_PREVIEW_COUNT && (
        <button
          type="button"
          className="text-[9px] underline text-slate-500 hover:text-slate-700"
          onClick={() => setExpanded(false)}
          data-testid={`note-show-less-${dispatchId}-${variant}`}
        >
          {t("Show fewer notes")}
        </button>
      )}
      {shown.map((n, i) => renderRow(n, i))}
    </div>
  );
}


const STATUS_FLOW = {
  // scheduled → manual choice (Site vs Shop) via the route dialog — see CompactCard
  en_route: { next: "on_site", label: "Arrived On Site", icon: MapPin, color: "bg-amber-600 hover:bg-amber-700", iconOnlyTitle: "Arrived On Site" },
  en_route_shop: { next: "at_shop", label: "Arrived At Shop", icon: MapPin, color: "bg-amber-600 hover:bg-amber-700", iconOnlyTitle: "Arrived At Shop" },
  at_shop: { next: "en_route", label: "Start Route to Site", icon: Truck, color: "bg-blue-600 hover:bg-blue-700", iconOnlyTitle: "Start Route to Site" },
  on_site: { next: "done_for_day", label: "Done for Day", icon: Check, color: "bg-slate-600 hover:bg-slate-700", iconOnlyTitle: "Done for Day" },
};
const STATUS_BADGES = {
  scheduled: "bg-blue-100 text-blue-700", en_route: "bg-purple-100 text-purple-700",
  en_route_shop: "bg-purple-100 text-purple-700", at_shop: "bg-indigo-100 text-indigo-700",
  on_site: "bg-amber-100 text-amber-700", completed: "bg-green-100 text-green-700",
  done_for_day: "bg-slate-200 text-slate-600", draft: "bg-slate-100 text-slate-700",
};
const STATUS_LABELS = {
  scheduled: "Scheduled", en_route: "En Route", en_route_shop: "En Route · Shop",
  at_shop: "At Shop", on_site: "On Site",
  completed: "Completed", done_for_day: "Done", draft: "Draft",
};

// Small visual indicator the tech sees in the top-right so they know
// whether the app is currently tracking them. The dot colour matches the
// tech's own assigned colour (from Settings → Technicians) when live so it
// maps 1:1 to how they appear on the dispatcher's Map tab.
function LivePill({ status, techColor }) {
  const { t } = useI18n();
  const configs = {
    live:        { label: "Live",        tip: "Location is being shared with dispatch.", dot: techColor || "#22c55e", pulse: true,  bg: "bg-emerald-50",  border: "border-emerald-200", text: "text-emerald-700" },
    "off-hours": { label: "Off-hours",   tip: "Tracking only runs during your shift window (05:30–17:00) on days you're scheduled.", dot: "#94a3b8", pulse: false, bg: "bg-slate-50",    border: "border-slate-200",   text: "text-slate-500" },
    blocked:     { label: "Blocked",     tip: "Location permission denied. Enable it in your browser / device settings, then reload.", dot: "#ef4444", pulse: false, bg: "bg-rose-50",     border: "border-rose-200",    text: "text-rose-700" },
    "no-signal": { label: "No Signal",   tip: "Your device can't get a GPS fix. Try stepping outside or checking that Location is enabled.", dot: "#f59e0b", pulse: false, bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700" },
    loading:     { label: "…",           tip: "Checking tracking status.", dot: "#cbd5e1", pulse: false, bg: "bg-slate-50",    border: "border-slate-200",   text: "text-slate-400" },
  };
  const c = configs[status] || configs.loading;
  return (
    <span
      title={t(c.tip)}
      data-testid={`gps-live-pill-${status}`}
      className={`inline-flex items-center gap-1.5 h-7 text-xs font-medium select-none cursor-help num ${c.text}`}
    >
      <span className="relative inline-flex w-2 h-2">
        {c.pulse && (
          <span className="absolute inline-flex w-full h-full rounded-full opacity-60 animate-ping" style={{ backgroundColor: c.dot }} />
        )}
        <span className="relative inline-flex w-2 h-2 rounded-full" style={{ backgroundColor: c.dot }} />
      </span>
      {t(c.label)}
    </span>
  );
}

export default function MyWorkOrders() {
  const navigate = useNavigate();
  // Pull the logged-in user so we can default the tech selector to
  // themselves when they're on a `technician` role account (mobile flow).
  const { user: currentUser } = useAuth();
  const { t, lang } = useI18n();
  const [technicians, setTechnicians] = useState([]);
  const dispatchCategories = useDispatchCategories();
  const [selectedTechId, setSelectedTechId] = useState("");
  // iter396 — the logged-in tech's own roster id. GPS / auto-clock always
  // bind to this; `selectedTechId` is only what's being VIEWED.
  const [myTechId, setMyTechId] = useState(null);
  const [tab, setTab] = useState(() => sessionStorage.getItem("field_tab") || "dispatch");
  useEffect(() => { sessionStorage.setItem("field_tab", tab); }, [tab]);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noRosterMatch, setNoRosterMatch] = useState(false);
  const [expandedWO, setExpandedWO] = useState(null);
  const [gpsActive, setGpsActive] = useState(false);
  // `gpsStatus` drives the "📍 Live" pill so the tech can see at a glance
  // whether the app is actually tracking them. States:
  //   live       — ping accepted by backend in the last cycle
  //   off-hours  — outside 05:30–17:00 window or not scheduled today (backend says skip)
  //   blocked    — browser permission denied by the user
  //   no-signal  — window is open but the device can't get a GPS fix
  //   loading    — initial state before first window evaluation completes
  const [gpsStatus, setGpsStatus] = useState("loading");
  // Tech-role users land on "today" by default (mobile flow — they care
  // about TODAY's run, not the full week). Office/PM keep the week view.
  const isTechView = currentUser?.role === "technician";
  // Card "Lookup" shortcut → open the Lookup tab straight on this WO / site / customer.
  useEffect(() => {
    const onLookup = (e) => {
      const d = e.detail || {};
      const entry = !isContract(d) && d.wo_id ? { type: "wo", id: d.wo_id, label: "Work Order" }
        : d.site_id ? { type: "site", id: d.site_id, label: "Site" }
        : d.client_id ? { type: "client", id: d.client_id, label: "Customer" } : null;
      sessionStorage.setItem("lookup_stack", JSON.stringify(entry ? [entry] : []));
      sessionStorage.setItem("lookup_q", entry ? "" : (d.client_name || d.wo_title || ""));
      setTab("lookup");
    };
    window.addEventListener("field:lookup", onLookup);
    return () => window.removeEventListener("field:lookup", onLookup);
  }, []);
  const [view, setView] = useState("today");
  // GPS permission state — gates the whole tech tab when denied. Updated
  // live via the Permissions API so a tech who enables it in Settings
  // gets the page unblocked without a refresh.
  const [locPermission, setLocPermission] = useState(null); // null = unchecked
  // Offline-queue indicator — number of pending pings/status updates
  // waiting for signal. Updates on every enqueue/flush via subscribe().
  const [offlinePending, setOfflinePending] = useState(0);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [inspectionModal, setInspectionModal] = useState(null); // {woId, inspections, templates}
  // Offline inspections: how many are finished on this device and still
  // waiting for signal, plus the last successful sync time.
  const [pendingInspections, setPendingInspections] = useState(0);
  const [syncingNow, setSyncingNow] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [signoffModal, setSignoffModal] = useState(null); // signoff summary data
  const [fieldNotesModal, setFieldNotesModal] = useState(null); // { woId, woNumber, notes }
  const [techDocsModal, setTechDocsModal] = useState(null); // { woId, woNumber, woDocs, partDocs }
  const [confirmCompleteId, setConfirmCompleteId] = useState(null); // dispatch id pending completion confirm
  const [completionModal, setCompletionModal] = useState(null); // { wo, dispatchId, dispatchHours, techName }
  // iter167 — `gpsInterval` is now an active-flag rather than an
  // interval id (native plugin owns its own watcher).  Kept the name
  // so the two evaluate() callsites below don't have to change shape.
  const gpsInterval = useRef(null);
  const gpsIntervalSec = useRef(null);

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const monthStart2 = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const monthEnd2 = useMemo(() => endOfMonth(currentDate), [currentDate]);
  const monthWeeks = useMemo(() => {
    const weeks = [];
    let dow = getDay(monthStart2);
    let offset = dow === 0 ? 6 : dow - 1;
    let start = addDays(monthStart2, -offset);
    for (let w = 0; w < 6; w++) {
      const week = Array.from({ length: 7 }, (_, d) => addDays(start, w * 7 + d));
      if (week.some(day => isSameMonth(day, currentDate))) weeks.push(week);
    }
    return weeks;
  }, [monthStart2, currentDate]);

  const fetchRange = useMemo(() => {
    if (view === "today") { const d = format(currentDate, "yyyy-MM-dd"); return { start: d, end: d }; }
    if (view === "week") return { start: format(weekStart, "yyyy-MM-dd"), end: format(weekEnd, "yyyy-MM-dd") };
    const mw = monthWeeks;
    if (!mw.length) return { start: format(monthStart2, "yyyy-MM-dd"), end: format(monthEnd2, "yyyy-MM-dd") };
    return { start: format(mw[0][0], "yyyy-MM-dd"), end: format(mw[mw.length - 1][6], "yyyy-MM-dd") };
  }, [view, currentDate, weekStart, weekEnd, monthStart2, monthEnd2, monthWeeks]);

  useEffect(() => {
    getTechnicians().then(r => {
      const active = r.data.filter(t => !t.is_archived);
      setTechnicians(active);
      if (active.length > 0) {
        // Tech-role users default to their own dispatch view — match by
        // email so a freshly-onboarded tech doesn't see whoever happened
        // to be saved in this browser's localStorage. They CAN still pick
        // another tech from the dropdown to view peers' schedules
        // (read-only intent; the action buttons stay gated by tech_id).
        let techId = null;
        const isTech = currentUser?.role === "technician" || currentUser?.role === "lead_technician";
        if (isTech && currentUser?.email) {
          const me = active.find(t => (t.email || "").toLowerCase() === currentUser.email.toLowerCase());
          if (me) { techId = me.id; setMyTechId(me.id); }
        }
        // iter301 — a tech whose login email matches no roster row used to
        // silently land on whoever was first in the list, i.e. someone
        // else's day. Say so instead.
        if (!techId && isTech) {
          setNoRosterMatch(true);
          setLoading(false);
          return;
        }
        if (!techId) {
          const saved = localStorage.getItem("my_tech_id");
          techId = (saved && active.find(t => t.id === saved)) ? saved : active[0].id;
        }
        setSelectedTechId(techId);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [currentUser?.email, currentUser?.role]);

  const loadDispatches = useCallback(async () => {
    if (!selectedTechId) return;
    try {
      const res = await listDispatches({ tech_id: selectedTechId, start_date: fetchRange.start, end_date: fetchRange.end });
      setDispatches(res.data);
    } catch { toast.error(t("Failed to load dispatches")); }
  }, [selectedTechId, fetchRange]);

  useEffect(() => { loadDispatches(); }, [loadDispatches]);
  useEffect(() => { if (selectedTechId) localStorage.setItem("my_tech_id", selectedTechId); }, [selectedTechId]);
  const gpsTechId = myTechId || selectedTechId;
  const peerView = !!myTechId && !!selectedTechId && selectedTechId !== myTechId;

  // Auto-start GPS when a tech is selected. GPS runs continuously while
  // the tech app is open; the only kill-switches are:
  //   1. Dispatcher-set per-tech `gps_enabled` flag (Dispatch page).
  //   2. Backend-enforced shift window (05:30–17:00 local) AND
  //      "must be scheduled today" gate. We pre-flight-check those here
  //      to avoid burning a cold GPS chip on a startup that the backend
  //      will reject anyway. Backend remains the source of truth — this
  //      is purely a battery optimisation.
  // The tech surface intentionally has NO on/off badge or toggle.
  useEffect(() => {
    if (!gpsTechId) return;
    let cancelled = false;
    const evaluate = async () => {
      const tech = technicians.find(t => t.id === gpsTechId);
      const techEnabled = !tech || tech.gps_enabled !== false;
      if (!techEnabled) { setGpsStatus("off-hours"); stopGPS(); return; }
      try {
        const r = await getGpsWindow(gpsTechId);
        if (cancelled) return;
        if (r?.data?.active) {
          if (!gpsInterval.current) startGPS();
        } else {
          setGpsStatus("off-hours");
          stopGPS();
        }
      } catch {
        // #146 (Group G): same `cancelled` guard as the success path —
        // the caller may have unmounted (or gpsTechId changed) by
        // the time the catch path fires, and starting GPS for a dead
        // effect leaks a setInterval that drains the phone battery.
        if (cancelled) return;
        // Couldn't reach the window endpoint — fall back to letting the
        // client try and the update-location endpoint's `skipped` path
        // will halt us anyway.
        if (!gpsInterval.current) startGPS();
      }
    };
    evaluate();
    // Re-evaluate every 5min so GPS auto-starts at 05:30 and auto-stops
    // at 17:00 without a page refresh, and so a fresh dispatch (tech
    // newly scheduled today) flips on without intervention.
    const poll = setInterval(evaluate, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(poll); };
    // `startGPS` is intentionally omitted from deps — re-creating the
    // GPS evaluator on every render would spam the navigator API and
    // burn battery. The 5min poll re-checks status independent of
    // closure age.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsTechId, technicians]);

  const startGPS = useCallback(() => {
    if (!gpsTechId) return;
    setGpsActive(true);
    gpsInterval.current = true; // active-flag for the evaluate() guards above
    // iter167 — Route through the native-shell abstraction.  On the
    // web this still uses navigator.geolocation in the foreground; on
    // the Capacitor iOS/Android shell it hands off to the native
    // background-geolocation plugin so GPS keeps firing even when the
    // screen is off.  Backend contract is identical either way —
    // adaptive `next_ping_s` (60s moving, 600s stationary) on the web,
    // distance-based emission on native.
    nativeStartTracking({
      onUpdate: async ({ lat, lng, source }) => {
        const payload = { tech_id: gpsTechId, lat, lng };
        try {
          const r = await updateTechLocation(payload);
          if (r && r.data && r.data.status === "skipped") {
            setGpsStatus("off-hours");
            stopGPS();
            return;
          }
          setGpsStatus(source === "native" ? "live-bg" : "live");
          // Adaptive cadence is web-only: native fires on distance,
          // not on a fixed tick.
          if (source === "web") {
            const nextS = Math.max(15, Math.min(1800, Number(r?.data?.next_ping_s) || 60));
            if (gpsIntervalSec.current !== nextS) {
              nativeSetForegroundInterval(nextS * 1000);
              gpsIntervalSec.current = nextS;
            }
          }
        } catch (err) {
          // Network down? Queue locally. The phone is still reading
          // satellites — we just can't reach our backend. The queue
          // flushes automatically on `online` event or every 30s.
          const isNetworkErr = !err?.response || err?.code === "ERR_NETWORK" || err?.message === "Network Error";
          if (isNetworkErr) {
            try { await enqueueOffline("tech-location", payload); } catch { /* IndexedDB unavailable */ }
            setGpsStatus("queued");
          }
          // else: silent — server-side errors don't change displayed status
        }
      },
      onError: (err) => {
        // err.code 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        if (err && err.code === 1) setGpsStatus("blocked");
        else setGpsStatus("no-signal");
      },
      minDistanceM: 25,
      foregroundIntervalMs: 60_000,
    });
    gpsIntervalSec.current = 60;
  }, [gpsTechId]);
  const stopGPS = () => {
    setGpsActive(false);
    nativeStopTracking();
    gpsInterval.current = null;
    gpsIntervalSec.current = null;
  };
  useEffect(() => () => { nativeStopTracking(); }, []);

  // Track GPS permission state for tech-role users so we can render
  // <LocationRequiredGate/> when it's not granted. Bypass + non-tech
  // users skip the gate entirely.
  useEffect(() => {
    if (!isTechView) { setLocPermission("granted"); return; }
    let cancelled = false;
    checkPermission().then(state => { if (!cancelled) setLocPermission(state); });
    const off = onPermissionChange((state) => setLocPermission(state));
    return () => { cancelled = true; off(); };
  }, [isTechView]);

  // Online/offline indicator + offline-queue subscription. Lives even on
  // PM accounts so we get parity on what's queued during a flaky network.
  useEffect(() => {
    const update = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    const refreshCount = async () => setOfflinePending(await offlinePendingCount());
    refreshCount();
    const off = subscribeOfflineQueue(refreshCount);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
      off();
    };
  }, []);

  // ── Offline inspections: boot the sync engine, keep the queue count
  // fresh, and pre-cache the working set whenever we have signal so the
  // tech is covered the moment they walk into a dead zone.
  useEffect(() => {
    initInspectionSync();
    const refresh = async () => {
      setPendingInspections(await pendingInspectionCount());
      setLastSync(await lastSyncAt());
    };
    refresh();
    const off = subscribeInspectionSync(refresh);
    return off;
  }, []);

  useEffect(() => {
    if (!isOnline) return;
    precacheBundle(14);
  }, [isOnline, fetchRange.start]);

  const syncNow = async () => {
    setSyncingNow(true);
    const res = await flushInspections({ manual: true });
    setSyncingNow(false);
    if (res?.error) toast.error("Sync failed — will keep retrying");
    else if (res?.synced) toast.success(`${res.synced} inspection${res.synced === 1 ? "" : "s"} uploaded`);
    else if (res?.skipped === "offline") toast.info("Still offline — queued");
    else if (res?.skipped === "empty") toast.success("Nothing waiting to upload");
    await precacheBundle(14);
  };

  // Open inspections for a WO. Works offline: falls back to the
  // pre-cached bundle and always offers the companywide library so the
  // tech can pull an inspection the manager didn't attach.
  const openInspections = async (woId) => {
    const dispatchId = dispatches.find(d => d.wo_id === woId)?.id;
    let inspections = [];
    let templates = [];
    let offline = false;
    try {
      const [inspRes, tmplRes] = await Promise.all([
        getInspections({ work_order_id: woId }),
        getInspectionTemplates(),
      ]);
      inspections = inspRes.data || [];
      templates = tmplRes.data || [];
    } catch {
      offline = true;
      const bundle = await cachedBundle();
      inspections = (bundle?.inspections || []).filter(i => i.work_order_id === woId);
      templates = bundle?.templates || [];
      if (!bundle) { toast.error("No cached inspections on this device yet"); return; }
    }
    // Local drafts can be ahead of both sources.
    const withState = await Promise.all(
      inspections.map(async i => ({ ...i, ui_state: await computeInspectionState(i) }))
    );
    setInspectionModal({ woId, dispatchId, inspections: withState, templates, offline });
  };

  // iter233 — jump straight into the WO's DOT inspection (create from the
  // seeded DOT template on first tap, resume/view on later taps).
  const openDotInspection = async (woId) => {
    try {
      const inspRes = await getInspections({ work_order_id: woId });
      const existing = (inspRes.data || []).find(i => i.is_dot);
      if (existing) {
        navigate(`/inspections/${existing.id}`, { state: { returnTo: "/field" } });
        return;
      }
      const tmplRes = await getInspectionTemplates();
      const dotTmpl = (tmplRes.data || []).find(t => t.is_dot);
      if (!dotTmpl) { toast.error("DOT inspection template not found"); return; }
      const created = await createInspectionFromTemplate(dotTmpl.id, woId, dispatches.find(d => d.wo_id === woId)?.id);
      navigate(`/inspections/${created.data.id}`, { state: { returnTo: "/field" } });
    } catch { toast.error("Could not open DOT inspection"); }
  };

  // Open sign-off for a WO
  const openSignoff = async (woId) => {
    try {
      const res = await getSignoffSummary(woId);
      setSignoffModal(res.data);
    } catch { toast.error("Failed to load sign-off summary"); }
  };

  // Open field notes for a WO
  const openFieldNotes = async (woId, woNumber, contract) => {
    if (contract?.contractJobId) {
      // Contract stop — notes live on the contract job, not a WO
      const d = dispatches.find(x => x.contract_job_id === contract.contractJobId);
      setFieldNotesModal({ woId: null, contractJobId: contract.contractJobId, woNumber: woNumber || d?.wo_title || "Contract job", notes: d?.field_notes || [] });
      return;
    }
    try {
      const res = await getFieldNotes(woId);
      setFieldNotesModal({ woId, woNumber, notes: res.data || [] });
    } catch { toast.error("Failed to load field notes"); }
  };

  // Open tech docs for a WO
  const openTechDocs = async (woId, woNumber) => {
    try {
      const res = await getWOTechInfo(woId);
      setTechDocsModal({ woId, woNumber, woDocs: res.data.wo_docs || [], partDocs: res.data.part_docs || [] });
    } catch { toast.error("Failed to load tech docs"); }
  };

  // Get current GPS position (returns {lat, lng} or null)
  const getCurrentPosition = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000, enableHighAccuracy: false }
    );
  });

  // Wraps a backend mutation in offline-aware error handling. On network
  // failure (no response received) the payload is queued for replay
  // when the device gets signal again. Returns true if the server
  // accepted the call OR if we successfully queued it offline; false
  // only on permanent server errors (4xx/5xx with body) — those signal
  // a real problem the UI should surface.
  const callOrQueue = async (kind, payload, apiCall) => {
    try {
      await apiCall();
      return { ok: true, queued: false };
    } catch (err) {
      const isNetworkErr = !err?.response || err?.code === "ERR_NETWORK" || err?.message === "Network Error";
      if (isNetworkErr) {
        try { await enqueueOffline(kind, payload); } catch { /* IndexedDB blocked */ }
        return { ok: true, queued: true };
      }
      return { ok: false, queued: false, error: err };
    }
  };

  const statusErr = (err, fallback = "Failed to update status") => {
    const d = err?.response?.data?.detail;
    return typeof d === "string" && d ? `${t(fallback)}: ${d}` : t(fallback);
  };

  const handleStatusChange = async (dispatchId, newStatus, extra = {}) => {
    // Capture GPS coords for the status history stamp
    const coords = await getCurrentPosition();
    const payload = { status: newStatus, ...extra };
    if (coords) { payload.lat = coords.lat; payload.lng = coords.lng; }

    if (newStatus === "done_for_day") {
      const dispatchPayload = { dispatch_id: dispatchId, ...payload };
      const r = await callOrQueue("dispatch-status", dispatchPayload, () => updateDispatchStatus(dispatchId, payload));
      if (r.ok) {
        setDispatches(prev => prev.map(d => d.id === dispatchId
          ? { ...d, dispatch_status: "done_for_day", logged_hours: extra.logged_hours ?? d.logged_hours }
          : d));
        const hrs = extra.logged_hours;
        const suffix = r.queued ? t(" (offline — will sync)") : "";
        toast.success((hrs != null ? t("Done for day · {h}h logged", { h: hrs }) : t("Done for day — WO remains open")) + suffix);
      } else {
        toast.error(statusErr(r.error));
      }
      return;
    }

    if (newStatus === "completed") {
      // Tech-initiated completion: mark BOTH the dispatch and the parent WO as
      // completed so the PM's dashboard flips to Complete immediately. Any
      // signature / signoff workflow is handled separately (PM-side, later).
      const dispatch = dispatches.find(d => d.id === dispatchId);
      const dispatchPayload = { dispatch_id: dispatchId, ...payload };
      const woPayload = dispatch?.wo_id ? { wo_id: dispatch.wo_id, status: "completed" } : null;
      const dr = await callOrQueue("dispatch-status", dispatchPayload, () => updateDispatchStatus(dispatchId, payload));
      const wr = woPayload
        ? await callOrQueue("wo-status", woPayload, () => updateWOStatus(woPayload))
        : { ok: true, queued: false };
      if (dr.ok && wr.ok) {
        setDispatches(prev => prev.map(d =>
          d.id === dispatchId ? { ...d, dispatch_status: "completed", wo_status: "completed" } : d
        ));
        const suffix = (dr.queued || wr.queued) ? t(" (offline — will sync)") : "";
        toast.success(t("WO marked Complete — PM dashboard updated") + suffix);
      } else {
        toast.error(statusErr(dr.error || wr.error, "Failed to complete WO"));
      }
      return;
    }

    // For en_route / en_route_shop / at_shop / on_site — update dispatch-level
    // status. WO-level status should only reflect actual site progress, so we
    // DON'T flip the WO status for shop-bound transitions (en_route_shop,
    // at_shop). Once the tech heads to site from shop, the en_route → on_site
    // path will mirror the WO as usual.
    const dispatch = dispatches.find(d => d.id === dispatchId);
    const mirrorsWOStatus = ["en_route", "on_site"].includes(newStatus);
    const dispatchPayload = { dispatch_id: dispatchId, ...payload };
    const woPayload = (mirrorsWOStatus && dispatch?.wo_id) ? { wo_id: dispatch.wo_id, status: newStatus } : null;
    const dr = await callOrQueue("dispatch-status", dispatchPayload, () => updateDispatchStatus(dispatchId, payload));
    const wr = woPayload
      ? await callOrQueue("wo-status", woPayload, () => updateWOStatus(woPayload))
      : { ok: true, queued: false };
    if (dr.ok && wr.ok) {
      setDispatches(prev => prev.map(d =>
        d.id === dispatchId
          ? { ...d, dispatch_status: newStatus, ...(mirrorsWOStatus ? { wo_status: newStatus } : {}) }
          : d
      ));
      const suffix = (dr.queued || wr.queued) ? t(" (offline — will sync)") : "";
      toast.success(t("Status → {status}", { status: t(STATUS_LABELS[newStatus] || newStatus) }) + suffix);
      if ((newStatus === "en_route" || newStatus === "en_route_shop") && !gpsActive) startGPS();
    } else {
      toast.error(statusErr(dr.error || wr?.error));
    }
  };

  const openMaps = (addr) => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`, "_blank");
  const prev = () => { if (view === "today") setCurrentDate(p => addDays(p, -1)); else if (view === "week") setCurrentDate(p => addDays(p, -7)); else setCurrentDate(p => addMonths(p, -1)); };
  const next = () => { if (view === "today") setCurrentDate(p => addDays(p, 1)); else if (view === "week") setCurrentDate(p => addDays(p, 7)); else setCurrentDate(p => addMonths(p, 1)); };
  const goToday = () => setCurrentDate(new Date());

  const dispatchesByDate = useMemo(() => {
    const m = {};
    dispatches.forEach(d => { if (!m[d.date]) m[d.date] = []; m[d.date].push(d); });
    return m;
  }, [dispatches]);
  const weekDays = useMemo(() => {
    const all = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    // Mon–Fri always; Sat/Sun only when this tech actually has something scheduled that day.
    return all.filter(d => (d.getDay() !== 0 && d.getDay() !== 6) || (dispatchesByDate[format(d, "yyyy-MM-dd")] || []).length > 0);
  }, [weekStart, dispatchesByDate]);

  // Schedule blocks (PTO / Contract Job / Site Visit …) live on the
  // technician record, not on dispatches — without reading them here the
  // tech's own board showed an empty day while the dispatcher saw "OFF".
  const myTimeOff = useMemo(() => (technicians.find(t => t.id === selectedTechId)?.time_off) || [], [technicians, selectedTechId]);
  const offForDate = useCallback((dayStr) => myTimeOff.filter(b => b.start_date && dayStr >= b.start_date && dayStr <= (b.end_date || b.start_date)), [myTimeOff]);

  const dl = lang === "es" ? { locale: esLocale } : undefined;
  const dateLabel = view === "today" ? format(currentDate, "EEE MMM d, yyyy", dl)
    : view === "week" ? `${format(weekStart, "MMM d", dl)} — ${format(weekEnd, "MMM d, yyyy", dl)}`
    : format(currentDate, "MMMM yyyy", dl);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900" /></div>;

  // iter301 — no roster row matches this login's email. Showing another
  // tech's schedule here would be worse than showing nothing.
  if (noRosterMatch) return (
    <div className="max-w-md mx-auto mt-16 text-center px-6" data-testid="no-roster-match">
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-6">
        <h2 className="text-base font-semibold text-amber-900">{t("We can't find your schedule")}</h2>
        <p className="text-sm text-amber-800 mt-2">
          {t("Your sign-in email {email} isn't on the technician roster yet, so we won't guess which schedule is yours.", { email: currentUser?.email || "" })}
        </p>
        <p className="text-sm text-amber-800 mt-2">
          {t("Call or text the office and ask them to add that exact email to your technician record — it takes a minute, then pull down to refresh.")}
        </p>
      </div>
    </div>
  );


  // Tech-role users MUST grant location permission. We block the page
  // until they do — they can still see the bypass-disabled banner if
  // they're not actually a tech. GPS works without cell signal (phone
  // reads satellites directly); pings just queue for sync.
  if (isTechView && locPermission && locPermission !== "granted") {
    return (
      <LocationRequiredGate
        state={locPermission}
        onGranted={() => setLocPermission("granted")}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="my-work-orders-page">
        <SubTabs tabs={[{ key: "dispatch", label: t("My Dispatch"), testId: "btn-field-tab-dispatch" }, { key: "lookup", label: t("Lookup"), testId: "btn-field-tab-lookup" }, { key: "map", label: t("Map"), testId: "btn-field-tab-map" }]}
          active={tab} onChange={setTab} testId="field-tab-pills" className="!px-0" dense />
        <div className="flex shrink-0 flex-wrap items-center gap-2 justify-between" data-testid="field-toolbar">
          <div className="flex items-center gap-2 flex-wrap">
            {tab === "dispatch" && (<>
            {/* Office users pick whose schedule to view (read-only) — same pill-select look as the
                Dispatch sort dropdown, sitting left of the view pills. Technicians only ever see their
                own: no picker, GPS stays on `myTechId`. */}
            {!isTechView && (
              <PillRail testId="field-tech-pill">
                <span className="relative inline-flex">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full" style={{ backgroundColor: technicians.find(t => t.id === selectedTechId)?.color || "#94a3b8" }} />
                  <select value={selectedTechId} onChange={(e) => setSelectedTechId(e.target.value)} data-testid="select-tech" title={t("View a technician's schedule")}
                    className={`appearance-none cursor-pointer whitespace-nowrap rounded-full bg-transparent pl-6 pr-7 py-1 text-xs font-medium outline-none transition-colors max-w-[150px] truncate ${peerView ? "seg-on" : "seg-off"}`}>
                    {!selectedTechId && <option value="">{t("Select tech")}</option>}
                    {technicians.map(tk => <option key={tk.id} value={tk.id}>{tk.name}{tk.id === myTechId ? t(" (me)") : ""}</option>)}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3 w-3 -translate-y-1/2 opacity-70 text-slate-500" />
                </span>
                {peerView && <Pill on onClick={() => setSelectedTechId(myTechId)} testId="btn-back-to-my-schedule" title={t("Back to my schedule")}>{t("Me")}</Pill>}
              </PillRail>
            )}
            <PillRail testId="field-view-pills">
              {[["today", "Day"], ["week", "Week"], ["month", "Month"]].map(([k, l]) => (
                <Pill key={k} on={view === k} onClick={() => setView(k)} testId={`btn-field-${k}`}>{t(l)}</Pill>
              ))}
            </PillRail>
            </>)}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {tab === "dispatch" && (<>
            <button type="button" className={`${btn("nav")} h-8 w-8 justify-center !px-0`} onClick={prev} data-testid="btn-field-prev" title={t("Previous")}><ChevronLeft className="w-4 h-4" /></button>
            <button type="button" className={`${btn("nav")} h-8 !px-2`} onClick={goToday} data-testid="btn-field-go-today">{t("Today")}</button>
            <button type="button" className={`${btn("nav")} h-8 w-8 justify-center !px-0`} onClick={next} data-testid="btn-field-next" title={t("Next")}><ChevronRight className="w-4 h-4" /></button>
            <span style={{ "--datejump-minw": "0px" }}><DateJump label={dateLabel} value={currentDate} onPick={setCurrentDate} testId="field-date-jump" /></span>
            </>)}
            {tab === "dispatch" && (
              <span className="text-xs text-slate-500 num whitespace-nowrap" data-testid="field-count">
                {t(dispatches.length === 1 ? "{n} stop" : "{n} stops", { n: dispatches.length })} · {dispatches.reduce((s, d) => s + (Number(d.hours) || 0), 0)}h
              </span>
            )}
            <span className="hidden sm:block h-4 w-px bg-slate-200" />
            <LivePill status={gpsStatus} techColor={technicians.find(t => t.id === selectedTechId)?.color} />
            {/* Inspection sync queue — always visible when something is
                waiting, with a manual "Sync now" for peace of mind. */}
            {(pendingInspections > 0 || !isOnline) && (
              <button
                type="button"
                onClick={syncNow}
                disabled={syncingNow}
                title={lastSync ? t("Last sync {time}", { time: new Date(lastSync).toLocaleTimeString() }) : t("Not synced yet")}
                className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[11px] font-semibold ${pendingInspections > 0 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-slate-100 border-slate-300 text-slate-700"}`}
                data-testid="inspection-sync-pill"
              >
                <span className={`inline-block w-2 h-2 rounded-full ${isOnline ? "bg-amber-500" : "bg-slate-500"}`} />
                {pendingInspections > 0
                  ? t(pendingInspections === 1 ? "{n} inspection waiting" : "{n} inspections waiting", { n: pendingInspections })
                  : t("Offline")}
                <span className="underline ml-1">{syncingNow ? t("Syncing…") : t("Sync now")}</span>
              </button>
            )}
            {/* Offline / queued indicator. Quiet when on-grid with an empty queue (the common case). */}
            {(offlinePending > 0 || !isOnline) && (
              <span
                title={isOnline
                  ? t(offlinePending === 1 ? "{n} update waiting to sync — backend not responding." : "{n} updates waiting to sync — backend not responding.", { n: offlinePending })
                  : t("You're offline. Updates are saving locally and will sync as soon as you reconnect.")}
                className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[11px] font-semibold select-none cursor-help ${isOnline ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-slate-100 border-slate-300 text-slate-700"}`}
                data-testid="offline-queue-pill"
              >
                <span className={`inline-block w-2 h-2 rounded-full ${isOnline ? "bg-blue-500" : "bg-slate-500"}`} />
                {isOnline ? t("Syncing {n}", { n: offlinePending }) : `${t("Offline")}${offlinePending ? ` · ${t("{n} queued", { n: offlinePending })}` : ""}`}
              </span>
            )}
          </div>
        </div>

        <div className="data-table-container p-3" style={{ height: "calc(100vh - 160px)", overflow: "auto" }}>
         <div className="flex-1 min-w-0">
          {tab === "lookup" && <FieldLookup />}
          {tab === "map" && <FieldMapLite />}
          {tab === "dispatch" && peerView && (
            <div className="max-w-[320px] mx-auto mb-2 rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-[10px] text-sky-800" data-testid="peer-view-banner">
              {t("Viewing {name}'s schedule — read-only.", { name: technicians.find(tk => tk.id === selectedTechId)?.name || "" })}
            </div>
          )}
          {tab === "dispatch" && view === "today" && <TodayView dispatches={dispatches} offBlocks={offForDate(format(currentDate, "yyyy-MM-dd"))} categories={dispatchCategories} expandedWO={expandedWO} setExpandedWO={setExpandedWO} handleStatusChange={handleStatusChange} openMaps={openMaps} openInspections={openInspections} openDotInspection={openDotInspection} openFieldNotes={openFieldNotes} openTechDocs={openTechDocs} confirmCompleteId={confirmCompleteId} setConfirmCompleteId={setConfirmCompleteId} setCompletionModal={setCompletionModal} reloadDispatches={loadDispatches} peerView={peerView} />}
          {tab === "dispatch" && view === "week" && <HorizWeekView weekDays={weekDays} dispatchesByDate={dispatchesByDate} offForDate={offForDate} categories={dispatchCategories} handleStatusChange={handleStatusChange} openMaps={openMaps} openInspections={openInspections} openDotInspection={openDotInspection} openFieldNotes={openFieldNotes} openTechDocs={openTechDocs} confirmCompleteId={confirmCompleteId} setConfirmCompleteId={setConfirmCompleteId} setCompletionModal={setCompletionModal} reloadDispatches={loadDispatches} peerView={peerView} />}
          {tab === "dispatch" && view === "month" && <MonthView monthWeeks={monthWeeks} currentDate={currentDate} dispatchesByDate={dispatchesByDate} offForDate={offForDate} categories={dispatchCategories}
            showWeekends={Object.keys(dispatchesByDate).some(k => { const d = new Date(k + "T12:00:00"); return (d.getDay() === 0 || d.getDay() === 6) && isSameMonth(d, currentDate) && dispatchesByDate[k].length > 0; })} />}
         </div>
        </div>

      {/* Inspection Modal */}
      {inspectionModal && <InspectionModal data={inspectionModal} onClose={() => setInspectionModal(null)} navigate={navigate} />}
      {/* Sign-Off Modal */}
      {signoffModal && <SignoffModal data={signoffModal} onClose={() => { setSignoffModal(null); loadDispatches(); }} />}
      {/* Field Notes Modal */}
      {fieldNotesModal && <FieldNotesModal data={fieldNotesModal} techId={selectedTechId} techName={technicians.find(t => t.id === selectedTechId)?.name || ""} onClose={() => setFieldNotesModal(null)} onNoteSaved={(woId, saved) => {
        if (fieldNotesModal.contractJobId) {
          setDispatches(prev => prev.map(x => x.contract_job_id === fieldNotesModal.contractJobId ? { ...x, field_notes: [...(x.field_notes || []), saved] } : x));
          setFieldNotesModal(m => m ? { ...m, notes: [...(m.notes || []), saved] } : m);
          return;
        }
        openFieldNotes(woId, fieldNotesModal.woNumber);
      }} />}
      {techDocsModal && <TechDocsModal data={techDocsModal} onClose={() => setTechDocsModal(null)} />}
      {/* Work-order close-out modal (note + voice→text + AI polish + signature + AM recap) */}
      {completionModal && (
        <WOCompletionModal
          open
          wo={completionModal.wo}
          dispatchId={completionModal.dispatchId}
          defaultSignedBy={completionModal.techName}
          onClose={() => setCompletionModal(null)}
          onCompleted={() => { setCompletionModal(null); loadDispatches(); }}
        />
      )}
    </div>
  );
}

/* Compact Card for Week Cells */
// iter270 — remember (per device) the newest dispatch note the tech has
// actually opened, so a note added after the dispatch was created shows a
// "NEW" dot until they read it. Works with or without push configured.
const noteSeenKey = (dispatchId) => `abs_notes_seen_${dispatchId}`;
const newestNoteAt = (notes) => (Array.isArray(notes) ? notes : [])
  .reduce((max, n) => (n?.created_at && n.created_at > max ? n.created_at : max), "");

// iter343 — full-detail view for a tech card (title / job # / tasks were cut off on the compact card).
function FieldDispatchDetail({ d, open, onClose, openMaps }) {
  const { t } = useI18n();
  if (!open) return null;
  const tasks = [...(d.parts || []).map(p => ({ ...p, co: false })), ...(d.co_groups || []).flatMap(g => (g.parts || []).map(p => ({ ...p, co: true })))];
  const notes = (d.notes || []).filter(n => (n.text || n.note || "").trim());
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`field-dispatch-detail-${d.id}`}>
        <DialogHeader>
          <DialogTitle className="text-base leading-snug break-words"><Tx text={d.wo_title} />{!d.wo_title && t("Untitled")} <span className="text-blue-600 font-mono text-sm whitespace-nowrap">{d.wo_number}</span></DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <div className="font-semibold text-slate-800">{d.client_name || "—"}</div>
            {d.site_name && <div className="text-slate-600">{d.site_name}</div>}
            {d.site_address && (
              <button type="button" className="text-blue-600 hover:underline text-left inline-flex items-start gap-1 mt-0.5" onClick={() => openMaps(d.site_address)}>
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />{d.site_address}
              </button>
            )}
          </div>
          {(d.contact_name || d.contact_phone) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-700">
              {d.contact_name && <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5 text-slate-400" />{d.contact_name}</span>}
              {d.contact_phone && <a href={`tel:${d.contact_phone}`} className="text-blue-600 hover:underline inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{d.contact_phone}</a>}
              {d.contact_email && <a href={`mailto:${d.contact_email}`} className="text-blue-600 hover:underline break-all">{d.contact_email}</a>}
            </div>
          )}
          <CrewLine crew={d.crew} className="!text-xs" testId={`detail-crew-${d.id}`} />
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline">{t("{h}h scheduled", { h: d.hours })}</Badge>
            {d.scheduled_date && <Badge variant="outline">{d.scheduled_date}</Badge>}
            {(d.lift_required || d.lift_note) && <Badge variant="outline" className="border-amber-300 text-amber-800">{t("Lift required")}{d.lift_note ? <> — <Tx text={d.lift_note} /></> : null}</Badge>}
            {d.dot_inspection_required && <Badge variant="outline" className={d.dot_inspection_complete ? "border-emerald-300 text-emerald-700" : "border-red-300 text-red-700"}>{d.dot_inspection_complete ? t("DOT inspection complete") : t("DOT inspection required")}</Badge>}
          </div>
          {tasks.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t("Tasks")}</div>
              <ul className="space-y-1">
                {tasks.map((p, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- display-only list
                  <li key={i} className="flex items-start gap-1.5 text-slate-800"><Wrench className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${p.co ? "text-purple-500" : "text-slate-500"}`} /><span>{Math.round(p.quantity)}x <Tx text={p.description} />{p.co ? <span className="text-purple-600 text-xs ml-1">(CO)</span> : null}</span></li>
                ))}
              </ul>
            </div>
          )}
          {d.description && <div><div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t("Scope")}</div><div className="whitespace-pre-wrap text-slate-700"><Tx text={d.description} /></div></div>}
          {notes.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t("Dispatch notes")}</div>
              <ul className="space-y-1.5">
                {notes.map((n, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- display-only list
                  <li key={i} className="rounded bg-amber-50 border border-amber-100 px-2 py-1 text-slate-800 whitespace-pre-wrap"><Tx {...txNote(n)} /></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function CompactCard({ d, handleStatusChange, openMaps, openInspections, openDotInspection, openFieldNotes, openTechDocs, confirmCompleteId, setConfirmCompleteId, setCompletionModal, reloadDispatches }) {
  const { t } = useI18n();
  const [notesPanel, setNotesPanel] = useState(false);
  const [notesSeenAt, setNotesSeenAt] = useState(() => {
    try { return localStorage.getItem(noteSeenKey(d.id)) || ""; } catch { return ""; }
  });
  const latestNoteAt = newestNoteAt(d.notes);
  const hasUnreadNotes = !!latestNoteAt && latestNoteAt > notesSeenAt;

  const openNotes = () => {
    setNotesPanel(true);
    if (latestNoteAt) {
      try { localStorage.setItem(noteSeenKey(d.id), latestNoteAt); } catch { /* private mode */ }
      setNotesSeenAt(latestNoteAt);
    }
    // iter271 — read receipt for the dispatcher (no notification back).
    markDispatchNotesSeen(d.id, { tech_id: d.tech_id || d.technician_id, tech_name: d.tech_name || "" })
      .catch(() => { /* offline: the dot still clears locally */ });
  };

  // Notes on arrival: tapping "Arrived On Site" surfaces the dispatcher's
  // instructions automatically instead of hoping the tech scrolls.
  const changeStatus = (dispatchId, next) => {
    handleStatusChange(dispatchId, next);
    if ((next === "on_site" || next === "at_shop") && (d.notes || []).length > 0) openNotes();
  };
  // Dispatch-level status drives the tech UI. Never fall back to the WO-level
  // status ("approved", "draft", etc.) — that mapping isn't part of STATUS_FLOW
  // and causes all action buttons to disappear.
  const st = d.dispatch_status || "scheduled";
  const statusAction = STATUS_FLOW[st];
  const ActionIcon = statusAction?.icon;
  const isDone = st === "done_for_day" || st === "completed";
  const [hoursInput, setHoursInput] = useState(String(d.logged_hours ?? d.hours ?? ""));
  const [doneBusy, setDoneBusy] = useState(false);
  const [doneModal, setDoneModal] = useState(false); // new Done-for-Day modal
  const [clockOutModal, setClockOutModal] = useState(false); // iter396 — contract Complete note
  const [routeDialog, setRouteDialog] = useState(false); // shop-vs-site picker
  const [detailOpen, setDetailOpen] = useState(false); // iter343 — full-detail sheet (title/job # were cut off)
  const contract = isContract(d);
  const band = bandColor(d);
  const [dotBusy, setDotBusy] = useState(false);
  const toggleContractDot = async () => {
    setDotBusy(true);
    try { await updateDispatch(d.id, { dot_inspection_complete: !d.dot_inspection_complete }); reloadDispatches?.(); }
    catch { toast.error(t("Could not update DOT status")); }
    finally { setDotBusy(false); }
  };
  return (
    <div className="relative rounded-lg border border-slate-200 bg-white p-1.5 pl-2.5 text-[9px] md:text-[11px] mb-1 overflow-hidden" style={{ boxShadow: `inset 4px 0 0 ${band}` }} data-testid={`field-dispatch-${d.id}`} data-kind={contract ? "contract" : "service"}>
      {/* 1. WO title — wo# (wraps to 2 lines; tap for the full detail view) */}
      <div className="flex items-start gap-1 mb-0.5">
        <button type="button" onClick={() => setDetailOpen(true)} title={t("Tap for full details")}
          className="font-semibold text-slate-800 text-[10px] md:text-xs leading-tight flex-1 min-w-0 text-left line-clamp-2 break-words hover:text-blue-700"
          data-testid={`field-dispatch-title-${d.id}`}>
          {d.wo_title ? <Tx text={d.wo_title} /> : t("Untitled")}
          {!contract && <span className="text-blue-600 font-bold ml-1 text-[8px] md:text-[10px] whitespace-nowrap">{d.wo_number}</span>}
        </button>
        <JobTag d={d} testId={`field-tag-${d.id}`} className="!h-auto !text-[7px] md:!text-[9px] !px-1 py-0 !rounded-full !font-semibold !tracking-normal leading-normal" />
        <Badge className={`text-[7px] md:text-[9px] px-1 py-0 shrink-0 ${STATUS_BADGES[st] || ""} ${st === "scheduled" ? "hidden" : ""}`}>{t(STATUS_LABELS[st] || st)}</Badge>
        <span className="text-[8px] md:text-[10px] text-slate-400 shrink-0">{d.hours}h</span>
      </div>
      <FieldDispatchDetail d={d} open={detailOpen} onClose={() => setDetailOpen(false)} openMaps={openMaps} />

      {/* 2. Customer name */}
      <div className="text-slate-600 font-medium text-[9px] md:text-[11px] truncate">{d.client_name || "—"}</div>
      {/* iter396 — teammates on the same job today */}
      <CrewLine crew={d.crew} testId={`crew-${d.id}`} />
      <PriorTechsLine techs={d.prior_techs} testId={`prior-techs-${d.id}`} />

      {/* iter233 — DOT inspection requirement. RED until the DOT report is
          completed, then GREEN. Tap → creates/resumes the DOT inspection. */}
      {d.dot_inspection_required && (
        <button
          type="button"
          disabled={contract && dotBusy}
          onClick={() => (contract ? toggleContractDot() : openDotInspection(d.wo_id))}
          className={`w-full mt-0.5 mb-0.5 flex items-center gap-1 rounded px-1.5 py-1 text-[9px] md:text-[11px] font-bold border touch-manipulation ${
            d.dot_inspection_complete
              ? "bg-emerald-50 border-emerald-300 text-emerald-700"
              : "bg-red-50 border-red-300 text-red-700"
          }`}
          data-testid={`dot-inspection-btn-${d.id}`}
        >
          {d.dot_inspection_complete ? <Check className="w-3 h-3 shrink-0" /> : <AlertTriangle className="w-3 h-3 shrink-0" />}
          {d.dot_inspection_complete ? t("DOT Inspection Complete") : contract ? t("DOT Inspection Required — tap when done") : t("DOT Inspection Required — tap to complete")}
        </button>
      )}

      {/* Contract stop — install details from the contract job */}
      {contract && d.install_details && (
        <div className="text-[8px] md:text-[10px] text-slate-700 whitespace-pre-wrap rounded bg-blue-50 border border-blue-100 px-1.5 py-1 mt-0.5" data-testid={`install-details-${d.id}`}>
          <span className="font-semibold text-blue-800 block text-[7px] md:text-[9px] uppercase tracking-wide">{t("Install details")}</span><Tx text={d.install_details} />
        </div>
      )}

      {/* 3. Site address (or "Pending site" if none on file) */}
      {d.site_address ? (
        <button className="text-blue-600 hover:underline truncate block w-full text-left text-[8px] md:text-[10px]" onClick={() => openMaps(d.site_address)}>
          <MapPin className="w-2.5 h-2.5 inline mr-0.5 shrink-0" />{d.site_address}
        </button>
      ) : (
        <div className="text-[8px] md:text-[10px] text-slate-400 italic" data-testid={`site-pending-${d.id}`}>
          <MapPin className="w-2.5 h-2.5 inline mr-0.5 shrink-0" />{t("Pending site")}
        </div>
      )}

      {/* iter396 — shared Field Access Notes (parking, gate code…) */}
      <SiteAccessLine notes={d.access_notes} testId={`site-access-${d.id}`} />

      {/* 4. Site Contact — name → email, phone → tel */}
      {d.contact_name && (
        <div className="flex items-center gap-1 text-[8px] md:text-[10px] mt-0.5" data-testid={`site-contact-${d.id}`}>
          <User className="w-2.5 h-2.5 text-slate-500 shrink-0" />
          {d.contact_email ? (
            <a href={`mailto:${d.contact_email}`} className="text-blue-600 hover:underline font-medium truncate max-w-[140px]">{d.contact_name}</a>
          ) : (
            <span className="text-slate-600 font-medium truncate max-w-[140px]">{d.contact_name}</span>
          )}
          {d.contact_phone && (
            <a href={`tel:${d.contact_phone}`} className="text-blue-600 hover:underline flex items-center gap-0.5 ml-1">
              <Phone className="w-2.5 h-2.5" />{d.contact_phone}
            </a>
          )}
        </div>
      )}

      {/* 5. Tasks (base WO parts) — wrench icon, slate by default;
          green emerald row + check when the tech has marked the row
          done in the DoneForDayModal. Same `dispatch_card_progress`
          drives the same green state on the dispatch board card and
          Open WO sidebar. */}
      {d.parts?.length > 0 && d.parts.map((p, i) => {
        const done = isPartChecked(p, d.dispatch_card_progress);
        return (
          // eslint-disable-next-line react/no-array-index-key -- tech task list; check state by idx in dispatch_card_progress
          <div key={`tt-${i}`} className={`text-[8px] md:text-[10px] font-bold truncate flex items-center gap-0.5 mt-0.5 ${done ? "text-emerald-800 bg-emerald-50 rounded px-1" : "text-slate-800"}`} data-testid={`tech-task-${d.id}-${i}`}>
            {done ? <Check className="w-2.5 h-2.5 text-emerald-600 shrink-0" /> : <Wrench className="w-2.5 h-2.5 text-slate-500 shrink-0" />}
            {Math.round(p.quantity)}x <Tx text={p.description} />
          </div>
        );
      })}

      {/* 6. CO Tasks — same row style as base tasks but purple wrench
          so the tech knows at a glance they're bills-on-a-change-order.
          Flattened: no per-CO header, no install_description preview. */}
      {(d.co_groups || []).flatMap(g => g.parts || []).map((p, i) => {
        const done = isPartChecked(p, d.dispatch_card_progress);
        return (
          // eslint-disable-next-line react/no-array-index-key -- flattened CO parts list; check state by idx
          <div key={`co-${i}`} className={`text-[8px] md:text-[10px] font-bold truncate flex items-center gap-0.5 ${done ? "text-emerald-800 bg-emerald-50 rounded px-1" : "text-slate-800"}`} data-testid={`co-task-${d.id}-${i}`}>
            {done ? <Check className="w-2.5 h-2.5 text-emerald-600 shrink-0" /> : <Wrench className="w-2.5 h-2.5 text-purple-500 shrink-0" />}
            {Math.round(p.quantity)}x <Tx text={p.description} />
          </div>
        );
      })}
      {/* Fallback: when COs carry labor hours but no promoted parts yet,
          keep a compact hours-only badge so techs still see them on the card. */}
      {(d.co_hours_breakdown || []).length > 0 && (d.co_groups || []).length === 0 && (
        <div className="flex flex-wrap gap-0.5 mt-0.5">
          {d.co_hours_breakdown.map(co => (
            <span key={co.co_id} className="text-[8px] md:text-[10px] bg-purple-50 text-purple-700 border border-purple-200 px-1 py-0 rounded" data-testid={`co-badge-${d.id}-${co.co_id}`}>
              + {co.co_number} · {co.hours}h
            </span>
          ))}
        </div>
      )}

      {/* Lift requirement — sits between tasks and dispatch notes as
          a plain text line underneath the wrench rows. Same logic
          (dispatcher-overridable) as before, just stripped of its old
          amber chip styling per UX request. */}
      {(d.lift_note || d.lift_required) && (
        <div className="text-[8px] md:text-[10px] text-slate-800 font-medium flex items-center gap-1 mt-0.5" data-testid={`lift-note-${d.id}`}>
          <span className="text-[9px] md:text-[11px] shrink-0">▲</span>
          {d.lift_note ? <Tx text={d.lift_note} /> : (d.lift_required ? t("Lift Required") : t("No lift required"))}
        </div>
      )}

      {/* 7. Dispatch notes — amber block mirroring the Field Notes look:
          small uppercase header, body stacked below. `d.notes` is now a
          timestamped list (append-only); show the most recent N entries
          by default with a "show all" toggle for the long-history case.
          #145 (Group G): rendering 100s of nodes in a field card
          freezes mobile Safari — cap by default. */}
      {Array.isArray(d.notes) && d.notes.length > 0 && (
        <NoteList
          notes={d.notes}
          variant="dispatch"
          dispatchId={d.id}
          renderRow={(n, i) => (
            <div key={n.id || i} className="italic text-amber-900 break-words">
              <span className="font-semibold not-italic mr-1">{abbreviateName(n.created_by)}{n.created_at ? ` · ${formatNoteDate(n.created_at)}` : ""}:</span><Tx {...txNote(n)} />
            </div>
          )}
        />
      )}

      {/* 8. Field notes — tech-recorded notes from previous visits on this WO. */}
      {Array.isArray(d.field_notes) && d.field_notes.length > 0 && (
        <NoteList
          notes={d.field_notes}
          variant="field"
          dispatchId={d.id}
          renderRow={(note, i) => {
            if (typeof note === "string") return <div key={i} className="break-words"><Tx text={note} /></div>;
            const author = abbreviateName(note.tech_name || note.created_by);
            const stamp = formatNoteDate(note.created_at);
            return (
              <div key={note.id || i} className="italic text-slate-700 break-words">
                <span className="font-semibold not-italic mr-1">{author}{stamp ? ` · ${stamp}` : ""}:</span><Tx {...txNote(note)} />
              </div>
            );
          }}
        />
      )}

      {/* Action icons — Row 1: utility buttons (Tech Docs · Inspections · Field Notes ·
          dispatch notes). Row 2: status progression (Route/Status · Done for Day · Complete). */}
      <div className="mt-1.5 space-y-1">
        <div className="flex flex-wrap gap-1.5 justify-start">
          {!contract && (
          <button onClick={() => openTechDocs(d.wo_id, d.wo_number)} className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-md border border-slate-300 bg-transparent text-slate-700 hover:bg-slate-50" title={t("Tech Docs")} data-testid={`btn-tech-docs-${d.id}`}>
            <FileText className="w-3.5 h-3.5" />
          </button>
          )}
          {!contract && (
          <button onClick={() => openInspections(d.wo_id)} className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-md border border-slate-300 bg-transparent text-slate-700 hover:bg-slate-50" title={t("Inspections")} data-testid={`btn-inspections-${d.id}`}>
            <ClipboardCheck className="w-3.5 h-3.5" />
          </button>
          )}
          <button onClick={() => openFieldNotes(d.wo_id, d.wo_number || d.wo_title, contract ? { contractJobId: d.contract_job_id } : undefined)} className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-md border border-slate-300 bg-transparent text-slate-700 hover:bg-slate-50" title={t("Field Notes")} data-testid={`btn-notes-${d.id}`}>
            <PenTool className="w-3.5 h-3.5" />
          </button>
          {/* Lookup shortcut — jump to this job's history / technician notes in the Lookup tab. */}
          <button onClick={() => window.dispatchEvent(new CustomEvent("field:lookup", { detail: d }))} className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-md border border-slate-300 bg-transparent text-slate-700 hover:bg-slate-50" title={t("Lookup — history & technician notes")} data-testid={`btn-lookup-${d.id}`}>
            <Search className="w-3.5 h-3.5" />
          </button>
          {/* iter269 — dispatcher instructions for this visit (materials,
              site contacts, special instructions). The card shows the most
              recent few inline; this opens the full timestamped history. */}
          {(Array.isArray(d.notes) ? d.notes.length : 0) > 0 && (
            <button
              onClick={openNotes}
              className={`relative flex items-center gap-1 h-7 px-2 text-[9px] md:text-[11px] font-semibold border rounded ${
                hasUnreadNotes
                  ? "bg-amber-200 hover:bg-amber-300 text-amber-900 border-amber-400"
                  : "bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-300"
              }`}
              title={hasUnreadNotes ? t("New dispatch note — tap to read") : t("Read all dispatch notes")}
              data-testid={`btn-dispatch-notes-${d.id}`}
              data-unread={hasUnreadNotes ? "true" : "false"}
            >
              <StickyNote className="w-3.5 h-3.5" />
              {t(d.notes.length === 1 ? "{n} note" : "{n} notes", { n: d.notes.length })}
              {hasUnreadNotes && (
                <span className="absolute -top-1 -right-1 flex items-center h-3.5 px-1 rounded-full bg-rose-600 text-white text-[7px] md:text-[9px] font-bold"
                      data-testid={`dispatch-notes-new-${d.id}`}>{t("NEW")}</span>
              )}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 justify-start">
          {st === "scheduled" ? (
            <button onClick={() => setRouteDialog(true)} title={t("Start Route")} aria-label={t("Start Route")}
              className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
              data-testid={`btn-start-route-${d.id}`}>
              <Truck className="w-3.5 h-3.5" />
            </button>
          ) : statusAction && !isDone && st !== "on_site" ? (
            <button onClick={() => changeStatus(d.id, statusAction.next)}
              title={t(statusAction.iconOnlyTitle || statusAction.label)}
              aria-label={t(statusAction.iconOnlyTitle || statusAction.label)}
              className={`flex items-center justify-center w-7 h-7 text-white rounded ${statusAction.color}`}
              data-testid={`btn-status-${d.id}`}>
              {ActionIcon && <ActionIcon className="w-3.5 h-3.5" />}
            </button>
          ) : null}
          {!isDone ? (
            <button onClick={() => setDoneModal(true)} title={t("Done for Day")} aria-label={t("Done for Day")}
              className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-md bg-slate-600 hover:bg-slate-700 text-white"
              data-testid={`btn-done-for-day-${d.id}`}>
              <Check className="w-3.5 h-3.5" />
            </button>
          ) : st === "done_for_day" ? (
            <div className="h-7 px-2 text-[10px] md:text-xs text-slate-500 font-medium bg-slate-100 rounded flex items-center gap-1" data-testid={`badge-done-${d.id}`}>
              <Check className="w-3.5 h-3.5" />{t("Done")}{d.logged_hours != null ? ` · ${d.logged_hours}h` : ""}
            </div>
          ) : null}
        {!isDone ? (
          <button
            onClick={async () => {
              if (contract) {
                // iter396 — Install / Overhead / Warranty: required clock-out note first
                setClockOutModal(true);
                return;
              }
              // Auto-batch-check every material on the card before
              // launching the customer sign-off modal. The UI immediately
              // reflects the green state — even before sign-off — and
              // BOM/Procurement/Budget/Invoice stay untouched.
              try {
                const allParts = [
                  ...(d.parts || []),
                  ...((d.co_groups || []).flatMap(g => g.parts || [])),
                ];
                const keys = Array.from(new Set(allParts.map(progressKeyFor).filter(Boolean)));
                if (keys.length) {
                  await checkAllCardProgress(d.wo_id, { keys, checked_by: d.tech_name || "Tech" });
                }
              } catch { /* silent — sign-off modal still opens */ }
              setCompletionModal({
                wo: { id: d.wo_id, wo_number: d.wo_number, title: d.wo_title, account_manager_email: d.account_manager_email },
                dispatchId: d.id,
                techName: d.tech_name,
                defaultHours: d.hours || 0,
              });
            }}
            title={t("Complete")} aria-label={t("Complete")}
            className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-md bg-green-600 hover:bg-green-700 text-white"
            data-testid={`btn-complete-${d.id}`}>
            <CheckCircle className="w-3.5 h-3.5" />
          </button>
        ) : st === "completed" ? (
          <div className="h-7 px-2 text-[10px] md:text-xs text-green-600 font-medium flex items-center gap-1" data-testid={`badge-completed-${d.id}`}>
            <CheckCircle className="w-3.5 h-3.5" />{t("Completed")}
          </div>
        ) : null}
        </div>
      </div>
      {notesPanel && (
        <DispatchNotesModal
          title={`${d.wo_number || ""} ${d.wo_title || ""}`.trim()}
          subtitle={d.client_name || d.site_name || ""}
          notes={d.notes}
          fieldNotes={d.field_notes}
          onClose={() => setNotesPanel(false)}
        />
      )}
      {/* Route picker: site vs shop. Keeps the button icon-only while still
          giving the tech a clear yes/no per the PM's request. */}
      <Dialog open={routeDialog} onOpenChange={setRouteDialog}>
        <DialogContent className="sm:max-w-[360px]" data-testid={`route-dialog-${d.id}`}>
          <DialogHeader>
            <DialogTitle>{t("Start Route")}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 mt-1">{t("Where are you headed first?")}</div>
          <div className="flex flex-col gap-2 mt-3">
            <Button
              className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white justify-start"
              data-testid={`btn-route-site-${d.id}`}
              onClick={() => { setRouteDialog(false); changeStatus(d.id, "en_route"); }}>
              <MapPin className="w-4 h-4 mr-2" />{t("Start Route to Site")}
            </Button>
            <Button
              className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white justify-start"
              data-testid={`btn-route-shop-${d.id}`}
              onClick={() => { setRouteDialog(false); changeStatus(d.id, "en_route_shop"); }}>
              <Wrench className="w-4 h-4 mr-2" />{t("Start Route to Shop")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Done-for-Day modal captures hours (default from settings) + lunch +
          notes, writes to the tech's timesheet, then flips dispatch to
          done_for_day. */}
      <DoneForDayModal
        open={doneModal}
        onOpenChange={(o) => {
          setDoneModal(o);
          // When the modal closes, refresh so the card mirrors any
          // checkbox toggles the tech made even if they hit Cancel.
          if (!o && reloadDispatches) reloadDispatches();
        }}
        techId={d.tech_id}
        techName={d.tech_name}
        woId={d.wo_id}
        contractJobId={contract ? d.contract_job_id : undefined}
        jobName={contract ? d.wo_title : undefined}
        parts={d.parts || []}
        coParts={(d.co_groups || []).flatMap(g => g.parts || [])}
        progressKeys={d.dispatch_card_progress || []}
        onSaved={async () => {
          try { await changeStatus(d.id, "done_for_day"); } catch { /* silent */ }
        }}
      />
      {contract && (
        <ClockOutNoteModal
          open={clockOutModal}
          onOpenChange={setClockOutModal}
          jobName={d.wo_title}
          kindLabel={kindMeta(jobKindOf(d)).label}
          onSubmit={async (note) => {
            try {
              await addContractFieldNote(d.contract_job_id, { text: note, tech_id: d.tech_id, tech_name: d.tech_name });
            } catch { toast.error(t("Could not save the note to the job")); throw new Error("note"); }
            appendTodayTimesheetNote(d.tech_id, { text: `${d.wo_title}: ${note}` }).catch(() => {});
            try { await changeStatus(d.id, "completed"); } catch { /* toast handled upstream */ }
            reloadDispatches?.();
          }}
        />
      )}
    </div>
  );
}

/* Today View */
function TodayView({ dispatches, offBlocks = [], categories, expandedWO, setExpandedWO, handleStatusChange, openMaps, openInspections, openDotInspection, openFieldNotes, openTechDocs, confirmCompleteId, setConfirmCompleteId, setCompletionModal, reloadDispatches, peerView }) {
  // Multi-stop day → compact pills; tap a pill to open that stop's full card (one at a time)
  const { t } = useI18n();
  const [activeStop, setActiveStop] = useState(null);
  const stops = [...dispatches].sort((a, b) => (a.stop_order ?? 0) - (b.stop_order ?? 0));
  const multi = stops.length > 1;
  const active = multi ? (stops.find(s => s.id === activeStop) || null) : null;
  if (!dispatches.length && !offBlocks.length) return <div className="text-center py-12 text-slate-400"><Wrench className="w-8 h-8 mx-auto mb-2 opacity-50" /><p className="text-sm">{t("No dispatches for today")}</p></div>;
  const card = (d) => peerView ? <PeerCard key={d.id} d={d} /> : <CompactCard key={d.id} d={d} handleStatusChange={handleStatusChange} openMaps={openMaps} openInspections={openInspections} openDotInspection={openDotInspection} openFieldNotes={openFieldNotes} openTechDocs={openTechDocs} confirmCompleteId={confirmCompleteId} setConfirmCompleteId={setConfirmCompleteId} setCompletionModal={setCompletionModal} reloadDispatches={reloadDispatches} />;
  return (
    <div className="max-w-[320px] md:max-w-xl mx-auto space-y-3" data-testid="today-view" data-stops={stops.length}>
      {offBlocks.map((b, i) => <OffBlockBadge key={b.id || i} block={b} categories={categories} testId={i === 0 ? "today-off-block" : `today-off-block-${i}`} />)}
      {multi && (
        <div className="space-y-2" data-testid="today-multi-stop">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{t("{n} stops today — tap one for details", { n: stops.length })}</div>
          <StopPills stops={stops} activeId={active?.id} size="lg" onPick={(s) => setActiveStop(prev => (prev === s.id ? null : s.id))} testIdPrefix="today-stop" />
          {active && <div data-testid={`today-stop-detail-${active.id}`}>{card(active)}</div>}
        </div>
      )}
      {!multi && stops.map(card)}
    </div>
  );
}

/* Horizontal Week View */
function HorizWeekView({ weekDays, dispatchesByDate, offForDate, categories, handleStatusChange, openMaps, openInspections, openDotInspection, openFieldNotes, openTechDocs, confirmCompleteId, setConfirmCompleteId, setCompletionModal, reloadDispatches, peerView }) {
  return (
    <div className="border border-slate-300 rounded-lg overflow-x-auto -mx-2 sm:mx-0">
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${weekDays.length}, minmax(140px, 1fr))`, minWidth: `${weekDays.length * 140}px` }}>
        {weekDays.map(day => <WeekDayHeader key={day.toISOString()} day={day} testId={`field-day-${format(day, "yyyy-MM-dd")}`} />)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${weekDays.length}, minmax(140px, 1fr))`, minWidth: `${weekDays.length * 140}px` }}>
        {weekDays.map(day => {
          const dayStr = format(day, "yyyy-MM-dd");
          const dayDisps = dispatchesByDate[dayStr] || [];
          const off = offForDate?.(dayStr) || [];
          const isToday = isSameDay(day, new Date());
          return (
            <div key={day.toISOString()} className={`p-1 border-r border-slate-300 last:border-r-0 ${isToday ? "bg-blue-50/20" : "bg-white"}`} style={{ minHeight: "200px" }}>
              {off.map((b, i) => <OffBlockBadge key={b.id || i} block={b} categories={categories} size="sm" testId={i === 0 ? `week-off-block-${dayStr}` : `week-off-block-${dayStr}-${i}`} />)}
              {dayDisps.length > 0 ? dayDisps.map(d => (
                peerView ? <PeerCard key={d.id} d={d} /> : <CompactCard key={d.id} d={d} handleStatusChange={handleStatusChange} openMaps={openMaps} openInspections={openInspections} openDotInspection={openDotInspection} openFieldNotes={openFieldNotes} openTechDocs={openTechDocs} confirmCompleteId={confirmCompleteId} setConfirmCompleteId={setConfirmCompleteId} setCompletionModal={setCompletionModal} reloadDispatches={reloadDispatches} />
              )) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Month View */
function MonthView({ monthWeeks, currentDate, dispatchesByDate, offForDate, categories, showWeekends }) {
  const { t } = useI18n();
  const dayNames = showWeekends ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] : ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const filterWeek = (week) => showWeekends ? week : week.filter(d => d.getDay() !== 0 && d.getDay() !== 6);
  return (
    <div className="border border-slate-300 rounded-lg overflow-x-auto -mx-2 sm:mx-0">
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${dayNames.length}, minmax(100px, 1fr))`, minWidth: `${dayNames.length * 100}px` }}>
        {dayNames.map(d => (
          <div key={d} className="bg-slate-50 p-1.5 text-center text-[9px] uppercase font-semibold text-slate-400 border-b border-r border-slate-300 last:border-r-0">{t(d)}</div>
        ))}
      </div>
      {monthWeeks.map((week, wi) => (
        // eslint-disable-next-line react/no-array-index-key -- fixed weeks for the current month
        <div key={`mw-${wi}`} style={{ display: "grid", gridTemplateColumns: `repeat(${dayNames.length}, minmax(100px, 1fr))`, minWidth: `${dayNames.length * 100}px` }}>
          {filterWeek(week).map(day => {
            const dayStr = format(day, "yyyy-MM-dd");
            const dayDisps = dispatchesByDate[dayStr] || [];
            const inMonth = isSameMonth(day, currentDate);
            const isToday = isSameDay(day, new Date());
            return (
              <div key={day.toISOString()} className={`bg-white p-1 min-h-[60px] border-b border-r border-slate-300 last:border-r-0 ${!inMonth ? "opacity-30" : ""} ${isToday ? "bg-blue-50/40" : ""}`}>
                <div className={`text-[10px] font-semibold mb-0.5 ${isToday ? "text-blue-600" : "text-slate-500"}`}>{format(day, "d")}</div>
                {(offForDate?.(dayStr) || []).map((b, i) => <OffBlockBadge key={b.id || i} block={b} categories={categories} size="sm" testId={i === 0 ? `month-off-block-${dayStr}` : `month-off-block-${dayStr}-${i}`} />)}
                {dayDisps.map(d => (<div key={d.id} className="rounded px-1 py-0.5 mb-0.5 text-[8px] bg-blue-50 border border-blue-100"><div className="font-semibold text-slate-700 truncate">{d.wo_title || d.wo_number}</div><div className="text-slate-500 truncate">{d.client_name}</div></div>))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* Inspection Modal — attached inspections + the tech pull path
   (choose from the companywide library when the manager didn't attach
   one). Templates are read-only to techs: they can perform, never edit. */
const STATE_LABELS = {
  not_started: { label: "Not Started", cls: "bg-slate-100 text-slate-600" },
  in_progress: { label: "In Progress", cls: "bg-blue-100 text-blue-700" },
  pending_sync: { label: "Completed – awaiting sync", cls: "bg-amber-100 text-amber-800" },
  synced: { label: "Synced", cls: "bg-green-100 text-green-700" },
};

function InspectionModal({ data, onClose, navigate }) {
  const { t } = useI18n();
  const { woId, dispatchId, inspections, templates = [], offline } = data;
  const [pulling, setPulling] = useState(false);
  const [showLibrary, setShowLibrary] = useState(inspections.length === 0);
  const [templateId, setTemplateId] = useState("");

  const openConduct = (inspectionId) => {
    onClose();
    navigate(`/inspections/${inspectionId}`, { state: { returnTo: "/field" } });
  };

  const pullFromLibrary = async () => {
    if (!templateId) { toast.error(t("Pick an inspection first")); return; }
    setPulling(true);
    try {
      const created = await createInspectionFromTemplate(templateId, woId, dispatchId);
      openConduct(created.data.id);
    } catch {
      toast.error(offline
        ? t("You need signal to start a new inspection — open one that's already on the card")
        : t("Could not start that inspection"));
    }
    setPulling(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl sm:rounded-t-xl z-10">
          <h3 className="font-bold text-slate-800"><ClipboardCheck className="w-5 h-5 inline mr-2" />{t("Inspections")}</h3>
          <div className="flex items-center gap-2">
            {offline && <Badge className="bg-slate-800 text-white border-0 text-[10px]" data-testid="inspections-offline-badge">{t("Offline copy")}</Badge>}
            <button onClick={onClose} data-testid="close-inspections-modal"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {inspections.length === 0 && <div className="text-center text-slate-400 text-sm py-4">{t("No inspection attached to this job yet — pull one from the library below.")}</div>}
          {inspections.map(insp => {
            const st = STATE_LABELS[insp.ui_state] || STATE_LABELS.in_progress;
            return (
              <div key={insp.id} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <span className="font-semibold text-sm text-slate-700">{insp.template_name || "Inspection"}</span>
                  <Badge className={`text-[10px] border-0 shrink-0 ${st.cls}`} data-testid={`insp-state-${insp.id}`}>{t(st.label)}</Badge>
                </div>
                {(insp.locations || []).length > 0 && (
                  <div className="text-xs text-slate-400 mb-2">{t(insp.locations.length === 1 ? "{n} location inspected" : "{n} locations inspected", { n: insp.locations.length })}</div>
                )}
                <Button
                  size="sm"
                  className="w-full mt-1 h-11 touch-manipulation"
                  onClick={() => openConduct(insp.id)}
                  data-testid={`btn-conduct-${insp.id}`}
                >
                  <ClipboardCheck className="w-4 h-4 mr-1" />{insp.status === "complete" ? t("View Report") : t("Continue Inspection")}
                </Button>
              </div>
            );
          })}

          <div className="border-t border-slate-200 pt-3">
            {!showLibrary ? (
              <Button variant="outline" size="sm" className="w-full h-11" onClick={() => setShowLibrary(true)} data-testid="btn-show-library">
                <Plus className="w-4 h-4 mr-1" />{t("Pull an inspection from the library")}
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("Inspection library")}</div>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger className="h-11 text-sm" data-testid="select-inspection-template">
                    <SelectValue placeholder={t("Choose an inspection…")} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  className="w-full h-11 touch-manipulation"
                  disabled={pulling || !templateId}
                  onClick={pullFromLibrary}
                  data-testid="btn-pull-inspection"
                >
                  {pulling ? t("Starting…") : t("Start inspection")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Customer Sign-Off Modal */
function SignoffModal({ data, onClose }) {
  const { t } = useI18n();
  const sigRef = useRef(null);
  const [signedBy, setSignedBy] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error(t("Signature required")); return; }
    if (!signedBy.trim()) { toast.error(t("Please enter name")); return; }
    setSubmitting(true);
    try {
      const sigData = sigRef.current.toDataURL("image/png");
      const res = await customerSignoff(data.wo_id, { signature_data: sigData, signed_by: signedBy });
      toast.success(t("Work order signed and completed!"));
      setDone(true);
    } catch { toast.error(t("Failed to submit signature")); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-800 text-lg">{t("Work Completion Sign-Off")}</h3>
          <div className="text-sm text-slate-500">{data.wo_number} — {data.title}</div>
          <div className="text-xs text-slate-400">{data.client_name}{data.site_name ? ` · ${data.site_name}` : ""}</div>
        </div>
        <div className="p-4">
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">{t("Materials Furnished & Installed")}</div>
          <div className="border border-slate-200 rounded-lg overflow-hidden mb-3">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr><th className="text-left px-3 py-1.5 text-xs font-semibold text-slate-500">{t("Item")}</th><th className="text-right px-3 py-1.5 text-xs font-semibold text-slate-500">{t("Qty")}</th><th className="text-right px-3 py-1.5 text-xs font-semibold text-slate-500">{t("Amount")}</th></tr>
              </thead>
              <tbody>
                {data.materials?.map((m, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- signoff materials list snapshot; no stable id
                  <tr key={`m-${i}`} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 text-slate-700"><Tx text={m.description} /></td>
                    <td className="px-3 py-1.5 text-right text-slate-600">{Math.round(m.quantity)}</td>
                    <td className="px-3 py-1.5 text-right text-slate-700">${(m.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
                {data.co_materials?.map((m, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- signoff CO materials snapshot
                  <tr key={`co-${i}`} className="border-t border-slate-100 bg-amber-50/30">
                    <td className="px-3 py-1.5 text-slate-700"><Tx text={m.description} /> <span className="text-[10px] text-amber-600">(CO)</span></td>
                    <td className="px-3 py-1.5 text-right text-slate-600">{Math.round(m.quantity)}</td>
                    <td className="px-3 py-1.5 text-right text-slate-700">${(m.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                <tr><td className="px-3 py-2 font-bold text-slate-800" colSpan={2}>{t("Total")}</td><td className="px-3 py-2 text-right font-bold text-slate-800 text-base">${(data.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td></tr>
              </tfoot>
            </table>
          </div>

          {!done && (
            <>
              <div className="mb-3">
                <label className="text-xs font-semibold text-slate-500 mb-1 block">{t("Signed By")}</label>
                <Input value={signedBy} onChange={(e) => setSignedBy(e.target.value)} placeholder={t("Customer name...")} className="text-sm" data-testid="input-signed-by" />
              </div>
              <div className="mb-3">
                <label className="text-xs font-semibold text-slate-500 mb-1 block">{t("Signature")}</label>
                <div className="border-2 border-slate-300 rounded-lg bg-white" data-testid="signature-pad">
                  <SignatureCanvas ref={sigRef} canvasProps={{ className: "w-full", style: { height: "120px", width: "100%" } }} />
                </div>
                <button onClick={() => sigRef.current?.clear()} className="text-xs text-blue-600 hover:underline mt-1">{t("Clear")}</button>
              </div>
              <Button className="w-full bg-green-600 hover:bg-green-700 text-white" onClick={handleSubmit} disabled={submitting} data-testid="btn-submit-signoff">
                <PenTool className="w-4 h-4 mr-2" />{submitting ? t("Submitting...") : t("Sign & Complete")}
              </Button>
            </>
          )}
          {done && (
            <div className="text-center py-4">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <div className="font-bold text-green-700 text-lg">{t("Work Order Signed & Complete")}</div>
              <div className="text-xs text-slate-400 mt-1">Email notification will be sent to customer (MOCKED)</div>
              <Button variant="outline" className="mt-3" onClick={onClose}>{t("Close")}</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* Field Notes Modal — internal tech notes attached to a WO */
function FieldNotesModal({ data, techId, techName, onClose, onNoteSaved }) {
  const { t, lang } = useI18n();
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { woId, woNumber, notes, contractJobId } = data;

  const handleSave = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const res = contractJobId
        ? await addContractFieldNote(contractJobId, { text: newNote.trim(), tech_id: techId, tech_name: techName })
        : await addFieldNote(woId, { text: newNote.trim(), tech_id: techId, tech_name: techName });
      setNewNote("");
      toast.success(t("Note saved"));
      onNoteSaved(woId, res?.data);
    } catch { toast.error(t("Failed to save note")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800 text-sm">{t("Field Notes")}</h3>
            <div className="text-xs text-slate-500">{woNumber}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {notes.length === 0 && <div className="text-center py-8 text-slate-400 text-xs">{t("No field notes yet. Add the first note below.")}</div>}
          {notes.map((n, i) => (
            <div key={n.id || i} className="bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid={`field-note-${i}`}>
              <div className="text-sm text-slate-800"><NoteText note={n} /></div>
              <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-slate-200">
                <span className="text-[10px] font-semibold text-blue-600">{abbreviateName(n.tech_name || n.created_by)}</span>
                <span className="text-[10px] text-slate-400">{formatNoteDate(n.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-slate-200">
          <div className="flex gap-2">
            <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder={t("Add a field note...")} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSave()} data-testid="input-field-note" />
            <Button size="sm" onClick={handleSave} disabled={!newNote.trim() || saving} data-testid="btn-save-field-note">
              {saving ? "..." : t("Save")}
            </Button>
          </div>
          <div className="text-[10px] text-slate-400 mt-1">{t("Internal only — visible to dispatchers and next technicians")}{lang === "es" && <span className="block" data-testid="note-translate-hint">{t("Your note is saved in English for the office (original Spanish kept).")}</span>}</div>
        </div>
      </div>
    </div>
  );
}


/* Tech Docs Modal — view WO + part library documents in the field */
function TechDocsModal({ data, onClose }) {
  const { t } = useI18n();
  const { woId, woNumber, woDocs, partDocs } = data;
  const totalDocs = woDocs.length + partDocs.length;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="tech-docs-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 shrink-0">
          <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            {t("Tech Docs — {wo}", { wo: woNumber })}
          </h3>
          <div className="text-[10px] text-slate-400 mt-0.5">{t(totalDocs === 1 ? "{n} document available" : "{n} documents available", { n: totalDocs })}</div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {totalDocs === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>{t("No tech documents for this work order")}</p>
            </div>
          )}
          {partDocs.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5 flex items-center gap-1">
                <Wrench className="w-3 h-3" /> {t("From Parts Library")}
              </div>
              <div className="space-y-1">
                {partDocs.map(doc => (
                  <a key={`p-${doc.id}`} href={getPartDocUrl(doc.part_id, doc.id)} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2.5 border rounded-lg bg-blue-50/30 hover:bg-blue-50 transition-colors" data-testid={`tech-part-doc-${doc.id}`}>
                    <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-700 truncate">{doc.filename}</div>
                      <div className="text-[10px] text-slate-400">{doc.part_name}{doc.part_number ? ` [${doc.part_number}]` : ""}</div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
          {woDocs.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5">{t("WO Documents")}</div>
              <div className="space-y-1">
                {woDocs.map(doc => (
                  <a key={`w-${doc.id}`} href={getWOTechDocUrl(woId, doc.id)} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2.5 border rounded-lg hover:bg-slate-50 transition-colors" data-testid={`tech-wo-doc-${doc.id}`}>
                    <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-700 truncate">{doc.filename}</div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="p-3 border-t border-slate-200 shrink-0">
          <Button variant="outline" size="sm" className="w-full" onClick={onClose}>{t("Close")}</Button>
        </div>
      </div>
    </div>
  );
}
